import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Lock, Shield, Key, Server, Users, Building2, FileCheck, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function ComplianceAndSecurity() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="Compliance and Security"
        description="GDPR-aligned data protection, secure authentication, and encrypted access. TED Monitor is built for compliance and security in EU procurement monitoring."
        path="/compliance-and-security"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/compliance-and-security" },
          { lang: "it", href: "https://tedmonitor.eu/it/conformita-sicurezza" },
          { lang: "x-default", href: "https://tedmonitor.eu/compliance-and-security" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Compliance and security for EU public procurement monitoring
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Public procurement workflows often involve sensitive information, structured monitoring activities, and
              professional usage requirements. TED Monitor is designed to support secure access, privacy-conscious
              handling of account data, and GDPR-aligned practices.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/eu-tender-monitoring"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                EU tender monitoring
              </Link>
              <Link
                to="/ted-procurement-alerts"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                TED procurement alerts
              </Link>
              <Link
                to="/european-public-procurement-software"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                European procurement software
              </Link>
              <Link
                to="/multilingual-access"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Multilingual access
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>

            <p className="mt-4 text-[11px] text-muted-foreground">
              Official source:{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                TED (Tenders Electronic Daily)
              </a>{" "}
              – Supplement to the Official Journal of the European Union.
            </p>
          </div>
        </section>

        {/* GDPR */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">GDPR-aligned data protection</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor follows GDPR-aligned principles for account and service data handling, supporting
              privacy-conscious processing and user rights in a professional monitoring context.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">This includes:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Processing aligned with transparency and purpose limitation principles",
                "Protection of personal account information and access credentials",
                "Respect for user rights (access, rectification, deletion where applicable)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Compliance is treated as a product requirement—not an afterthought.
            </p>

            <p className="mt-4">
              <Link to="/privacy-policy" className="text-primary hover:underline font-medium text-sm">
                Read our Privacy Policy →
              </Link>
            </p>
          </div>
        </section>

        {/* Authentication */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Key className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Secure authentication and access control
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Platform access is protected by secure authentication mechanisms designed to reduce the risk of
              unauthorized access and to keep accounts logically isolated.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Operational outcomes:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Account-level data remains protected behind authentication",
                "Users only see information associated with their account context",
                "Sensitive actions are restricted to authenticated users",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Encryption / infrastructure */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Encrypted access and secure infrastructure
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor uses encryption and secure communication practices to protect platform usage and
              system-to-system interactions.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Lock className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Data in transit</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Key className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Account access</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Server className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">System communications</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              The infrastructure is designed with reliability and data integrity in mind to support continuous
              monitoring workflows.
            </p>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Learn about the data source →
              </Link>
            </p>
          </div>
        </section>

        {/* Professional use */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileCheck className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Designed for professional usage requirements
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              For professional teams, security and compliance are baseline requirements. TED Monitor is built to
              support:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Security-first access for professional workflows",
                "Compliance-oriented documentation and policies",
                "Reliable operation for continuous monitoring",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground mb-3 text-sm">This makes TED Monitor suitable for:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-background rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Companies</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Consultants</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <FileCheck className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Professional firms</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Shield className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Teams handling sensitive workflows</p>
              </div>
            </div>

            <p className="mt-4">
              <Link
                to="/european-public-procurement-software"
                className="text-primary hover:underline font-medium text-sm"
              >
                Explore European procurement software →
              </Link>
            </p>
          </div>
        </section>

        {/* Trust */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Trust and compliance in EU procurement
              </h2>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              In EU procurement monitoring, trust is built through transparent policies, secure access, and
              privacy-conscious handling of account data. TED Monitor is designed to provide a secure and
              compliance-oriented environment for organizations monitoring public opportunities across Europe.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Use a secure, compliance-oriented procurement platform
            </h2>
            <p className="text-base mb-6 opacity-90">
              Start monitoring EU public procurement with a platform designed for professional usage requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/eu-tender-monitoring" className="text-muted-foreground hover:text-foreground">
                EU Tender Monitoring
              </Link>
              <Link to="/ted-procurement-alerts" className="text-muted-foreground hover:text-foreground">
                Procurement Alerts
              </Link>
              <Link to="/european-public-procurement-software" className="text-muted-foreground hover:text-foreground">
                Procurement Software
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
